import { companyBlack, userBlack, warning } from '../../../../assets/hostedassets';
import { RPT_COLUMN_TOOLTIP } from './RelatedPartyTransactions/relatedPartyTransactions_constants';
// TASK_TRACKER_STATUS/isTerminalFailure are generic tracker-status helpers, also used by
// LegalCases and RiskIndicators — the canonical definitions live in the shared constants module;
// re-exported here so existing imports of this file keep working.
import { TASK_TRACKER_STATUS, isTerminalFailure } from '../../../../utils/constants/taskTrackerStatus';
export { TASK_TRACKER_STATUS, isTerminalFailure };

export const AION_INGEST_RELATED_PARTIES_DATA = 'AION_INGEST_RELATED_PARTIES_DATA';

export const TAB_LOCK_COMPLETED_STATUSES = new Set(['COMPLETE', 'FAILED', 'FAILURE']);

// Statuses that indicate ingestion is still in progress on the BE — keep polling.
export const TASK_TRACKER_PENDING_STATUSES: string[] = [TASK_TRACKER_STATUS.TODO, TASK_TRACKER_STATUS.TODO_ASYNC];

// Which UI block the Related Party section renders. Precedence: PROCESSING > DATA > ERROR > EMPTY.
export const VIEW_STATE = {
    PROCESSING: 'processing',
    DATA: 'data',
    ERROR: 'error',
    EMPTY: 'empty',
} as const;

export type ViewState = (typeof VIEW_STATE)[keyof typeof VIEW_STATE];

export const SEVERITY_ENUM = {
    0: 'NA',
    1: 'LOW',
    2: 'MEDIUM',
    3: 'HIGH',
    4: 'SEVERE',
};

export const SEVERITY_DEG = {
    NA: 0,
    LOW: 1,
    MEDIUM: 2,
    HIGH: 3,
    SEVERE: 4,
};

export const getImage = (type: string) => {
    switch (type) {
        case 'PERSON':
            return userBlack;
        case 'COMPANY':
        case 'LIMITED_LIABILITY_PARTNERSHIP':
        case 'LLP':
        case 'OTHERS':
            return companyBlack;
        default:
            return warning;
    }
};

export function getRelatedPartyHeaders(isPDFReport = false) {
    return [
        { key: 'relatedPartyName', label: 'Related Party Name' },
        {
            key: 'relation',
            label: 'Relation',
            tooltip:
                'It describes the type of connection between the company and the related party, such as ownership, control, common directors etc.',
        },
        {
            key: 'relationTo',
            label: 'Relation To',
        },
        { key: 'details', label: 'Details' },
        {
            key: 'risk',
            label: 'Risk',
            tooltip:
                'It displays the potential risk associated with the related party, categorized by severity (Severe, High, Medium, Low).',
        },
        ...(!isPDFReport
            ? [
                  { key: 'transactions', label: 'Transactions', tooltip: RPT_COLUMN_TOOLTIP },
                  { key: 'alerts', label: 'Alerts' },
              ]
            : []),
    ];
}

export const getBannerStatusColors = (status: string) => {
    let color = '#BCBCBC';
    let banner = '#F7F7F7';
    switch (status) {
        case 'SEVERE':
            color = '#FFFFFF';
            banner = '#DF666A';
            break;
        case 'HIGH':
            color = '#DF666A';
            banner = '#FFF4F5';
            break;
        case 'MEDIUM':
            color = '#FF7A0E';
            banner = '#FFF4EB';
            break;
        case 'LOW':
            color = '#FFB800';
            banner = '#FFFCEB';
            break;
        default:
            return { color: '#BCBCBC', banner: '#F7F7F7' };
    }
    return {
        color,
        banner,
    };
};

// edgeTypes whose shareholding % is framed as "Holding: X%"; every other edgeType renders
// "Designation • X%" when a designation is present, or the bare percentage/NA otherwise.
export const HOLDING_PREFIX_EDGE_TYPES = new Set<EdgeType>(['SUBSIDIARY', 'ASSOCIATE']);

export const getEdgeTypeLabels = (edgeType: string) => {
    switch (edgeType) {
        case 'DIRECTOR':
            return 'Directors';
        case 'KMP':
            return 'Key Management Personnel';
        case 'FORMER_DIRECTOR':
            return 'Former Directors';
        case 'HOLDING_COMPANY':
            return 'Holding Companies';
        case 'SUBSIDIARY':
            return 'Subsidiaries';
        case 'JOINT_VENTURE':
            return 'Joint Ventures';
        case 'ASSOCIATE':
            return 'Associates';
        case 'SISTER_CONCERN':
            return 'Sister Concerns';
        case 'SHAREHOLDER':
            return 'Shareholders more than 5%';
        default:
            return edgeType;
    }
};

export const relationTypeMap = {
    PERSON: {
        COMPANY: {
            DIRECTOR: 'is a Director',
            FORMER_DIRECTOR: 'is a Former Director',
            DESIGNATED_PARTNER: 'is a Designated Partner',
            FORMER_DESIGNATED_PARTNER: 'is a Former Designated Partner',
        },
        LIMITED_LIABILITY_PARTNERSHIP: {
            DIRECTOR: 'is a Director',
            FORMER_DIRECTOR: 'is a Former Director',
            DESIGNATED_PARTNER: 'is a Designated Partner',
            FORMER_DESIGNATED_PARTNER: 'is a Former Designated Partner',
        },
    },
    COMPANY: {
        PERSON: {
            DIRECTOR: 'has a Director',
            FORMER_DIRECTOR: 'has a Former Director',
            SHAREHOLDER: 'has a Shareholder',
            KMP: `has a ${getEdgeTypeLabels('KMP')}`,
        },
        COMPANY: {
            HOLDING_COMPANY: 'has a Holding Company',
            SUBSIDIARY: 'is a Subsidiary',
            SISTER_CONCERN: 'has a Sister Concern',
            JOINT_VENTURE: 'has a Joint Venture',
            ASSOCIATE: 'is an Associate',
        },
        LIMITED_LIABILITY_PARTNERSHIP: {
            HOLDING_COMPANY: 'has a Holding Company',
            SUBSIDIARY: 'is a Subsidiary',
            SISTER_CONCERN: 'has a Sister Concern',
            JOINT_VENTURE: 'has a Joint Venture',
            ASSOCIATE: 'is an Associate',
        },
    },
    LIMITED_LIABILITY_PARTNERSHIP: {
        PERSON: {
            DIRECTOR: 'has a Director',
            FORMER_DIRECTOR: 'has a Former Director',
            SHAREHOLDER: 'has a Shareholder',
            KMP: `has a ${getEdgeTypeLabels('KMP')}`,
        },
        COMPANY: {
            HOLDING_COMPANY: 'has a Holding Company',
            SUBSIDIARY: 'is a Subsidiary',
            SISTER_CONCERN: 'has a Sister Concern',
            JOINT_VENTURE: 'has a Joint Venture',
            ASSOCIATE: 'is an Associate',
        },
        LIMITED_LIABILITY_PARTNERSHIP: {
            HOLDING_COMPANY: 'has a Holding Company',
            SUBSIDIARY: 'is a Subsidiary',
            SISTER_CONCERN: 'has a Sister Concern',
            JOINT_VENTURE: 'has a Joint Venture',
            ASSOCIATE: 'is an Associate',
        },
    },
    OTHERS: {
        PERSON: {
            DIRECTOR: 'has a Director',
            FORMER_DIRECTOR: 'has a Former Director',
            SHAREHOLDER: 'has a Shareholder',
            KMP: `has a ${getEdgeTypeLabels('KMP')}`,
        },
        COMPANY: {
            HOLDING_COMPANY: 'has a Holding Company',
            SUBSIDIARY: 'is a Subsidiary',
            SISTER_CONCERN: 'has a Sister Concern',
            JOINT_VENTURE: 'has a Joint Venture',
            ASSOCIATE: 'is an Associate',
        },
        LIMITED_LIABILITY_PARTNERSHIP: {
            HOLDING_COMPANY: 'has a Holding Company',
            SUBSIDIARY: 'is a Subsidiary',
            SISTER_CONCERN: 'has a Sister Concern',
            JOINT_VENTURE: 'has a Joint Venture',
            ASSOCIATE: 'is an Associate',
        },
    },
};

// Why this is not just more relationTypeMap cells: `type` is an open vendor value (FIRM, TRUST,
// SOCIETY, GOVERNMENT, ASSOCIATION_OF_PERSONS, 'null', missing, ...), so a (parent, child) grid can
// never be complete — prod had 33 unmapped combinations for these three edges in two months.
// These labels apply to ANY type pair the map does not list; a map entry always wins (that is
// how KMP reads "has a ..." for a business parent with a PERSON child and "is a ..." otherwise).
export const relationEdgeFallbackLabels: Record<string, string> = {
    SHAREHOLDER: 'is a Shareholder',
    KMP: `is a ${getEdgeTypeLabels('KMP')}`,
    SISTER_CONCERN: relationTypeMap.COMPANY.COMPANY.SISTER_CONCERN,
};

export const ALERTS_UNAVAILABLE_TEXT = 'NA';

export const HOLDING_LABEL = 'Holding';
